import { map } from 'rxjs/operators';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import * as dayjs from 'dayjs';
import { DashboardComponent } from 'src/app/pages/dashboards/dashboard/dashboard.component';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
interface OnlineUser {
	id: number;
	name: string;
	email: string;
	loginTime: Date;
	lastActivity: Date;
	timeLastActivity: string;
	userType: string;
	inactivityTime: string;
	status: 'active' | 'idle' | 'away';
}

@Component({
	selector: 'app-caller-tracking',
	templateUrl: './caller-tracking.component.html',
	styleUrls: ['./caller-tracking.component.scss'],
})
export class CallerTrackingComponent implements OnInit, OnDestroy {
	Math = Math; // Make Math available in template
	onlineUsers: any[] = [];
	totalUsers: number = 0;
	//filteredUsers: OnlineUser[] = [];
	searchTerm: string = '';
	selectedUserType: string = 'all';
	selectedStatus: string = 'all';
	selectedUserId: number | null = null;
	userInfo: any = null;
	timeToRefresh: number = 10;

	// Pagination
	currentPage: number = 1;
	itemsPerPage: number = 10;
	totalItems: number = 0;

	// Timer subscription for polling
	timerSubscription: Subscription | null = null;
	timerDials: Subscription | null = null;

	userTypes: string[] = ['Administrator', 'Teacher', 'Student', 'Support'];
	statusOptions = [
		{ value: 'all', label: 'All Status' },
		{ value: 'active', label: 'Active' },
		{ value: 'idle', label: 'Idle' },
		{ value: 'away', label: 'Away' },
	];

	totalDials = this.onlineUsers.reduce((sum, u) => sum + (u.dials || 0), 0);
	totalAppointments = this.onlineUsers.reduce((sum, u) => sum + (u.appointments || 0), 0);
	idleAlerts = this.onlineUsers.filter((u) => dayjs().diff(dayjs(u.date_time), 'minute') >= 60).length;
	appointmentsToday = 0;

	avatarColors = ['#1abc9c', '#3498db', '#9b59b6', '#e67e22', '#e74c3c', '#2ecc71', '#f39c12', '#16a085'];

	dialsToday: number = 0;
	dashboardStats: any = null;
	selectedFilter = 'ALL';
	filteredUsers = [];
	totalLeadsQC: number = 0;

	constructor(
		private dashboardSrv: DashboardService,
		private utils: UtilsService,
		private modalSrv: NgbModal,
	) {}

	ngOnInit(): void {
		this.getUsersActivity();
		this.loadDashboardStats();

		this.timerSubscription = interval(this.timeToRefresh * 1000).subscribe(() => {
			this.getUsersActivity();
			this.loadDashboardStats();
		});
	}

	ngOnDestroy(): void {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
	}

	getStatusClass(status: string): string {
		switch (status) {
			case 'active':
				return 'bg-success';
			case 'idle':
				return 'bg-warning';
			case 'away':
				return 'bg-danger';
			default:
				return 'bg-secondary';
		}
	}

	getUserTypeClass(userType: string): string {
		switch (userType) {
			case 'ADM':
				return 'badge bg-primary';
			case 'CUS':
				return 'badge bg-info';
			case 'CAL':
				return 'badge bg-success';
			default:
				return 'badge bg-secondary';
		}
	}

	refreshData(): void {}

	getUsersActivity(): void {
		const payload = {
			timezone: this.utils.getUserTimeZone(),
			start_date: dayjs().subtract(5, 'hour').format('YYYY-MM-DD HH:mm:ss'),
			end_date: dayjs().add(1, 'hour').format('YYYY-MM-DD HH:mm:ss'),
		};
		this.dashboardSrv.getUsersActivity(payload).subscribe((res: any) => {
			this.onlineUsers = res.body.actions.map((user) => {
				//user.inactivityTime = this.utils.getTimeDifference(user.date_time);
				return {
					...user,
					date_time: dayjs.utc(user.date_time).toDate(),
				};
			});

			this.dashboardSrv.setOnlineUsersCount(this.onlineUsers.length);
			this.setFilter(this.selectedFilter);
			this.totalUsers = res.body.totalUsers;
			this.applyStatsToUsers();
		});
	}

	getUnixTimestamp(dateString: string): number {
		return Math.floor(new Date(dateString).getTime());
	}

	getUserInfoObject(name, company_name) {
		return {
			name: name,
			company_name: company_name || '',
		};
	}

	openActionsModal(modal, user: any) {
		this.selectedUserId = user.user_id; // Set the user_id as needed
		this.userInfo = this.getUserInfoObject(user.full_name, user.user_company_name);
		this.modalSrv.open(modal, {
			size: 'xl',
			centered: true,
			scrollable: true,
			backdrop: 'static',
		});
	}

	getInactivityClass(date: Date): string {
		const minutes = dayjs().diff(dayjs(date), 'minute');

		if (minutes <= 30) return 'idle-green';
		if (minutes <= 59) return 'idle-orange';
		return 'idle-red';
	}

	getInitials(name: string): string {
		if (!name) return '';

		const parts = name.trim().split(' ');
		const first = parts[0]?.charAt(0) || '';
		const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

		return (first + last).toUpperCase();
	}

	getAvatarColor(name: string): string {
		let hash = 0;

		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}

		const index = Math.abs(hash % this.avatarColors.length);

		return this.avatarColors[index];
	}

	loadDashboardStats() {
		this.dashboardSrv.getDashboardStats().subscribe((res: any) => {
			this.dashboardStats = res.body;

			this.totalDials = this.dashboardStats.totalDials;
			this.appointmentsToday = this.dashboardStats.appointmentsToday;
			this.totalLeadsQC = this.dashboardStats.totalLeadsQC || 0;

			this.applyStatsToUsers();
		});
	}

	formatTime(seconds: number) {
		if (!seconds) return '-';

		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);

		if (hours > 0) {
			return `${hours}h ${minutes}m`;
		}

		return `${minutes}m`;
	}

	// applyStatsToUsers() {
	// 	if (!this.dashboardStats || !this.onlineUsers) return;

	// 	const dials = this.dashboardStats.dialsByAgent;
	// 	const talk = this.dashboardStats.talkTimeByAgent;
	// 	const appointments = this.dashboardStats.appointmentsByUser;

	// 	this.onlineUsers.forEach((user) => {
	// 		user.dials = dials[user.full_name] || 0;

	// 		const seconds = talk[user.full_name] || 0;
	// 		user.talkTime = this.formatTime(seconds);
	// 		user.appointments = appointments?.[String(user.user_id)] || 0;
	// 	});
	// }

	applyStatsToUsers() {
    if (!this.dashboardStats || !this.onlineUsers) return;

    const dials        = this.dashboardStats.dialsByAgent || {};
    const talk         = this.dashboardStats.talkTimeByAgent || {};
    const appointments = this.dashboardStats.appointmentsByUser || {};
    const lastCallByAgent = this.dashboardStats.lastCallByAgent || {};

    // normalizar keys para evitar mismatch de espacios/capitalización
    const normalizedLastCall: { [key: string]: any } = {};
    Object.keys(lastCallByAgent).forEach(key => {
        normalizedLastCall[key.trim().toLowerCase()] = lastCallByAgent[key];
    });

    this.onlineUsers.forEach((user) => {
        user.dials        = dials[user.full_name] || 0;
        user.talkTime     = this.formatTime(talk[user.full_name] || 0);
        user.appointments = appointments?.[String(user.user_id)] || 0;
        user.lastCall     = normalizedLastCall[user.full_name?.trim().toLowerCase()] || null;
    });
}

	setFilter(type: string) {
		this.selectedFilter = type;

		if (type === 'ALL') {
			this.filteredUsers = this.onlineUsers;
			return;
		}

		this.filteredUsers = this.onlineUsers.filter((user) => user.portal === type);
	}

	getCount(type: string) {
		return this.onlineUsers.filter((user) => user.portal === type).length;
	}
}
